import React, { useState, useEffect } from 'react';
import { colors, typography, spacing } from '../styles/design-system';
import { useAuth } from '../contexts/AuthContext';

interface HeaderProps {
  serverStatus: 'online' | 'offline' | 'checking';
  activeTab?: 'matches' | 'config' | 'admin';
  onTabChange?: (tab: 'matches' | 'config' | 'admin') => void;
}

const Header: React.FC<HeaderProps> = ({ serverStatus, activeTab = 'matches', onTabChange }) => {
  const { currentUser, isAdmin, logout } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 메뉴 클릭 시 모바일 메뉴 닫기
  const handleTabChange = (tab: 'matches' | 'config' | 'admin') => {
    if (onTabChange) {
      onTabChange(tab);
    }
    setIsMobileMenuOpen(false);
  };

  // 전체 날짜시간 포맷
  const formatDateTimeFull = (date: Date) => {
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // 축약형 날짜시간 포맷 (MM.DD HH:mm)
  const formatDateTimeShort = (date: Date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month}.${day} ${hours}:${minutes}`;
  };

  const getStatusColor = () => {
    switch (serverStatus) {
      case 'online': return colors.status.success;
      case 'offline': return colors.status.error;
      case 'checking': return colors.status.warning;
      default: return colors.gray[500];
    }
  };

  const getStatusText = () => {
    switch (serverStatus) {
      case 'online': return '정상';
      case 'offline': return '오프라인';
      case 'checking': return '확인중';
      default: return '-';
    }
  };

  return (
    <header style={styles.header}>
      <div style={styles.container}>
        {/* 좌측: 로고 + 서버상태 + 시간 */}
        <div style={styles.leftSection}>
          <h1
            style={{...styles.title, cursor: 'pointer'}}
            onClick={() => handleTabChange('matches')}
            title="경기 조회"
          >
            Sports Analyzer
          </h1>
          <div style={styles.statusGroup} className="header-status-group">
            <div style={styles.status}>
              <span style={{...styles.statusDot, backgroundColor: getStatusColor()}}></span>
              <span>{getStatusText()}</span>
            </div>
            <span style={styles.separator}>|</span>
            <div style={styles.datetime} className="header-datetime-full">
              {formatDateTimeFull(currentTime)}
            </div>
            <div style={styles.datetime} className="header-datetime-short">
              {formatDateTimeShort(currentTime)}
            </div>
          </div>
        </div>

        {/* 모바일 햄버거 버튼 */}
        <button
          style={styles.hamburger}
          className="header-hamburger"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="메뉴 열기"
        >
          <span style={styles.hamburgerLine}></span>
          <span style={styles.hamburgerLine}></span>
          <span style={styles.hamburgerLine}></span>
        </button>

        {/* 우측: 텍스트 링크 형식 메뉴 + 사용자 정보 (데스크톱) */}
        <div style={styles.rightSection} className="header-right-section">
          {onTabChange && (
            <nav style={styles.nav}>
              <button
                style={{
                  ...styles.navLink,
                  ...(activeTab === 'matches' ? styles.navLinkActive : {})
                }}
                onClick={() => handleTabChange('matches')}
              >
                경기 조회
              </button>
              <span style={styles.navSeparator}>|</span>
              <button
                style={{
                  ...styles.navLink,
                  ...(activeTab === 'config' ? styles.navLinkActive : {})
                }}
                onClick={() => handleTabChange('config')}
              >
                서버 환경설정
              </button>
            </nav>
          )}
          {currentUser && (
            <div style={styles.userSection}>
              {onTabChange && isAdmin ? (
                <button
                  style={styles.userNameButton}
                  onClick={() => handleTabChange('admin')}
                >
                  <span style={styles.userNameText}>{currentUser.display_name}</span>
                  <span style={styles.userRole}>
                    ({currentUser.role === 'administrator' ? '관리자' : '매니저'})
                  </span>
                </button>
              ) : (
                <span style={styles.userName}>
                  <span style={styles.userNameText}>{currentUser.display_name}</span>
                  <span style={styles.userRole}>
                    ({currentUser.role === 'administrator' ? '관리자' : '매니저'})
                  </span>
                </span>
              )}
              {/* 매니저는 관리자 페이지 접근 불가하므로 헤더에서 로그아웃 */}
              {!isAdmin && (
                <button
                  style={styles.logoutButton}
                  onClick={logout}
                >
                  로그아웃
                </button>
              )}
            </div>
          )}
        </div>

        {/* 모바일 메뉴 (드롭다운) */}
        {isMobileMenuOpen && (
          <div style={styles.mobileMenu} className="header-mobile-menu">
            {onTabChange && (
              <>
                <button
                  style={{
                    ...styles.mobileNavLink,
                    ...(activeTab === 'matches' ? styles.mobileNavLinkActive : {})
                  }}
                  onClick={() => handleTabChange('matches')}
                >
                  경기 조회
                </button>
                <button
                  style={{
                    ...styles.mobileNavLink,
                    ...(activeTab === 'config' ? styles.mobileNavLinkActive : {})
                  }}
                  onClick={() => handleTabChange('config')}
                >
                  서버 환경설정
                </button>
              </>
            )}
            {currentUser && (
              <div style={styles.mobileUserSection}>
                {onTabChange && isAdmin ? (
                  <button
                    style={{
                      ...styles.mobileNavLink,
                      ...(activeTab === 'admin' ? styles.mobileNavLinkActive : {})
                    }}
                    onClick={() => handleTabChange('admin')}
                  >
                    {currentUser.display_name} ({currentUser.role === 'administrator' ? '관리자' : '매니저'})
                  </button>
                ) : (
                  <span style={styles.mobileUserInfo}>
                    {currentUser.display_name} ({currentUser.role === 'administrator' ? '관리자' : '매니저'})
                  </span>
                )}
                {!isAdmin && (
                  <button
                    style={styles.mobileLogoutButton}
                    onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                  >
                    로그아웃
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: {
    backgroundColor: colors.white,
    color: colors.black,
    padding: `${spacing[4]} 0`,
    borderBottom: `1px solid ${colors.gray[300]}`,
  },
  container: {
    padding: `0 ${spacing[7]}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    flexWrap: 'wrap',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[6],
  },
  title: {
    margin: 0,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.black,
    whiteSpace: 'nowrap' as const,
  },
  statusGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
    whiteSpace: 'nowrap' as const,
  },
  statusDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  separator: {
    color: colors.gray[300],
    fontSize: typography.fontSize.sm,
  },
  datetime: {
    fontFamily: typography.fontFamilyMono,
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
    whiteSpace: 'nowrap' as const,
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
  },
  navLink: {
    padding: `${spacing[1]} ${spacing[2]}`,
    backgroundColor: 'transparent',
    color: colors.gray[500],
    border: 'none',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.normal,
    cursor: 'pointer',
    transition: 'color 0.2s',
    whiteSpace: 'nowrap' as const,
    outline: 'none',
  },
  navLinkActive: {
    color: colors.black,
    // fontWeight 변경 제거: 굵기 변경 시 텍스트 너비가 달라져 탭 위치 흔들림 발생
  },
  navSeparator: {
    color: colors.gray[300],
    fontSize: typography.fontSize.sm,
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[6],
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
    paddingLeft: spacing[4],
    borderLeft: `1px solid ${colors.gray[300]}`,
  },
  userNameButton: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    padding: `${spacing[2]} ${spacing[3]}`,
    backgroundColor: 'transparent',
    color: colors.gray[700],
    border: 'none',
    fontSize: typography.fontSize.sm,
    cursor: 'pointer',
    transition: 'all 0.2s',
    outline: 'none',
  },
  userName: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    padding: `${spacing[2]} ${spacing[3]}`,
    color: colors.gray[700],
    fontSize: typography.fontSize.sm,
  },
  userRole: {
    fontSize: typography.fontSize.xs,
    color: colors.gray[500],
  },
  logoutButton: {
    padding: `${spacing[2]} ${spacing[3]}`,
    backgroundColor: 'transparent',
    color: colors.gray[500],
    border: `1px solid ${colors.gray[300]}`,
    fontSize: typography.fontSize.sm,
    cursor: 'pointer',
    transition: 'all 0.2s',
    outline: 'none',
  },
  userNameText: {
    whiteSpace: 'nowrap' as const,
  },
  // 햄버거 버튼 (기본 숨김, CSS로 모바일에서 표시)
  hamburger: {
    display: 'none',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    gap: '4px',
    padding: spacing[2],
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    outline: 'none',
  },
  hamburgerLine: {
    width: '20px',
    height: '2px',
    backgroundColor: colors.gray[600],
    borderRadius: '1px',
  },
  // 모바일 메뉴
  mobileMenu: {
    display: 'none',
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: colors.white,
    borderBottom: `1px solid ${colors.gray[300]}`,
    padding: spacing[4],
    flexDirection: 'column' as const,
    gap: spacing[2],
    zIndex: 1000,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  mobileNavLink: {
    display: 'block',
    width: '100%',
    padding: `${spacing[3]} ${spacing[4]}`,
    backgroundColor: 'transparent',
    color: colors.gray[600],
    border: 'none',
    fontSize: typography.fontSize.base,
    textAlign: 'left' as const,
    cursor: 'pointer',
    outline: 'none',
    borderRadius: '4px',
  },
  mobileNavLinkActive: {
    backgroundColor: colors.gray[100],
    color: colors.black,
  },
  mobileUserSection: {
    borderTop: `1px solid ${colors.gray[200]}`,
    paddingTop: spacing[3],
    marginTop: spacing[2],
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[2],
  },
  mobileUserInfo: {
    padding: `${spacing[3]} ${spacing[4]}`,
    color: colors.gray[700],
    fontSize: typography.fontSize.sm,
  },
  mobileLogoutButton: {
    display: 'block',
    width: '100%',
    padding: `${spacing[3]} ${spacing[4]}`,
    backgroundColor: 'transparent',
    color: colors.status.error,
    border: 'none',
    fontSize: typography.fontSize.base,
    textAlign: 'left' as const,
    cursor: 'pointer',
    outline: 'none',
    borderRadius: '4px',
  },
};

export default Header;